import axios from 'axios';
import { encryptBlob, type FileEncryption } from './fileCrypto';

/**
 * Sube una foto desde el navegador a R2 con la misma regla de la app: lado mayor 1.600 px (2.000 px para
 * documentos), JPEG 70 %, sin EXIF (el lienzo no copia los metadatos) y miniatura de 320 px. El archivo va
 * directo a R2 con un enlace firmado; el API solo registra el adjunto.
 */
export interface UploadedAttachment {
  id: number;
  status: string;
  thumbUrl?: string | null;
  url?: string | null;
}

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image'));
    };
    img.src = url;
  });

export const resizeToJpeg = async (img: HTMLImageElement, maxSide: number, quality: number) => {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas');
  ctx.drawImage(img, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('jpeg');
  return { blob, width, height };
};

const putBlob = async (url: string, blob: Blob, contentType: string) => {
  const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob });
  if (!res.ok) throw new Error(`R2 ${res.status}`);
};

export const uploadPhoto = async (
  file: File,
  owner: { ownerType: string; ownerId?: number | null; ownerRef?: string | null; routeId?: number | null; kind?: string },
  clientRequestId: string,
): Promise<UploadedAttachment> => {
  const img = await loadImage(file);
  const kind = owner.kind ?? 'receipt';
  const main = await resizeToJpeg(img, kind === 'receipt' || kind === 'document' ? 2000 : 1600, 0.7);
  const thumb = await resizeToJpeg(img, 320, 0.6);
  const { data } = await axios.post('attachments/upload-url', {
    ...owner,
    kind,
    contentType: 'image/jpeg',
    sizeBytes: main.blob.size,
    thumbBytes: thumb.blob.size,
    width: main.width,
    height: main.height,
    takenAt: new Date().toISOString(),
    clientRequestId,
  });
  if (data?.attachment?.status !== 'uploaded') {
    // Empresa con cifrado: la foto y la miniatura se cifran aquí antes de subirlas.
    const enc: FileEncryption | null = data.upload.encryption ?? null;
    const mainBody = enc ? await encryptBlob(main.blob, enc, 'main') : main.blob;
    const type = enc ? 'application/octet-stream' : 'image/jpeg';
    await putBlob(data.upload.url, mainBody, type);
    if (data.thumbUpload) await putBlob(data.thumbUpload.url, enc ? await encryptBlob(thumb.blob, enc, 'thumb') : thumb.blob, type);
    const done = await axios.post(`attachments/${data.attachment.id}/complete`, {});
    return done.data.attachment;
  }
  return data.attachment;
};

export const newRequestId = (kind: string) =>
  `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
